import { useEffect, useState } from 'react';
import { Text, View } from 'react-native';

import { Button, Sheet, TextField } from '@/components/ui';
import { upsertFormulario } from '@/services/firebase/formularios';
import { colors, typography } from '@/theme/tokens';
import type { Formulario } from '@/types/domain';

interface Props {
  visible: boolean;
  onClose: () => void;
  formulario: Formulario | null;
}

export function FormularioSheet({ visible, onClose, formulario }: Props) {
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [stock, setStock] = useState('0');
  const [stockMin, setStockMin] = useState('10');
  const [ubicacion, setUbicacion] = useState('');
  const [notas, setNotas] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    setError(null);
    setNombre(formulario?.nombre ?? '');
    setDescripcion(formulario?.descripcion ?? '');
    setStock(String(formulario?.stock ?? 0));
    setStockMin(String(formulario?.stockMin ?? 10));
    setUbicacion(formulario?.ubicacion ?? '');
    setNotas(formulario?.notas ?? '');
  }, [visible, formulario]);

  const onSave = async () => {
    setError(null);
    if (!nombre.trim()) return setError('El nombre es obligatorio.');
    const stockN = Number(stock);
    const stockMinN = Number(stockMin);
    if (Number.isNaN(stockN) || stockN < 0) return setError('Stock inválido.');
    if (Number.isNaN(stockMinN) || stockMinN < 0) return setError('Stock mínimo inválido.');

    setSaving(true);
    try {
      await upsertFormulario({
        id: formulario?.id,
        nombre: nombre.trim(),
        descripcion: descripcion.trim() || undefined,
        stock: stockN,
        stockMin: stockMinN,
        ubicacion: ubicacion.trim() || undefined,
        notas: notas.trim() || undefined,
      });
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      size="md"
      title={formulario ? 'Editar formulario' : 'Nuevo formulario'}
      subtitle="Papeles preimpresos: castración, autorizaciones, vacuna rabia, certificados, etc."
      footer={
        <>
          <Button label="Cancelar" variant="ghost" onPress={onClose} />
          <Button label={formulario ? 'Guardar cambios' : 'Crear'} onPress={onSave} loading={saving} />
        </>
      }
    >
      <TextField
        label="Nombre"
        placeholder="Formulario de castración"
        value={nombre}
        onChangeText={setNombre}
      />
      <TextField
        label="Descripción (opcional)"
        placeholder="Para qué se usa este formulario"
        value={descripcion}
        onChangeText={setDescripcion}
      />

      <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
        <TextField
          containerStyle={{ flex: 1, minWidth: 140 }}
          label="Stock actual"
          value={stock}
          onChangeText={setStock}
          keyboardType="numeric"
        />
        <TextField
          containerStyle={{ flex: 1, minWidth: 140 }}
          label="Stock mínimo"
          value={stockMin}
          onChangeText={setStockMin}
          keyboardType="numeric"
          hint="Si stock ≤ mínimo, salta alerta."
        />
      </View>

      <TextField
        label="Ubicación (opcional)"
        placeholder="Cajón recepción, archivador del consultorio…"
        value={ubicacion}
        onChangeText={setUbicacion}
      />

      <TextField
        label="Notas (opcional)"
        value={notas}
        onChangeText={setNotas}
        multiline
      />

      {error ? (
        <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
          <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>{error}</Text>
        </View>
      ) : null}
    </Sheet>
  );
}
